import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Award, Pencil, BookOpen, Sparkles, ListChecks,
  Flame, Lightbulb, PenTool, Star, Trophy, Lock, CheckCircle
} from 'lucide-react';

const WritingBadges = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({});

  const iconMap = {
    'pencil': Pencil,
    'book-open': BookOpen,
    'award': Award,
    'sparkles': Sparkles,
    'list': ListChecks,
    'flame': Flame,
    'lightbulb': Lightbulb,
    'pen-tool': PenTool,
  };

  useEffect(() => {
    fetchBadges();
  }, [childId]);

  const fetchBadges = async () => {
    try {
      const response = await api.get(`/badges/writing-achievements/${childId}`);
      setBadges(response.data.badges || []);
      setStats(response.data.writing_stats || {});
    } catch (error) {
      console.error('Failed to fetch badges:', error);
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const earnedCount = badges.filter(b => b.earned).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-400 to-orange-500 text-white py-4 px-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-learning">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Writing Badges</h1>
              <p className="text-xs text-white/80">Earn them all!</p>
            </div>
          </div>
          
          <Badge className="bg-white/20 text-white border-0">
            {earnedCount}/{badges.length}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Overview */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-6 text-white">
            <h2 className="text-xl font-bold mb-4">Your Writing Journey</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/20 rounded-xl p-3 text-center">
                <p className="text-3xl font-bold">{stats.total_writings || 0}</p>
                <p className="text-xs text-white/80">Stories Written</p>
              </div>
              <div className="bg-white/20 rounded-xl p-3 text-center">
                <p className="text-3xl font-bold">{stats.total_words || 0}</p>
                <p className="text-xs text-white/80">Total Words</p>
              </div>
              <div className="bg-white/20 rounded-xl p-3 text-center">
                <p className="text-3xl font-bold">{stats.writing_streak || 0}</p>
                <p className="text-xs text-white/80">Day Streak</p>
              </div>
              <div className="bg-white/20 rounded-xl p-3 text-center">
                <p className="text-3xl font-bold">{earnedCount}</p>
                <p className="text-xs text-white/80">Badges Earned</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Badges Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {badges.map((badge, idx) => {
            const IconComponent = iconMap[badge.icon] || Award;
            
            return (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card 
                  className={`border-0 shadow-md transition-all ${
                    badge.earned 
                      ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200' 
                      : 'bg-slate-50'
                  }`}
                  data-testid={`badge-${badge.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                        badge.earned 
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' 
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        {badge.earned ? (
                          <IconComponent className="w-8 h-8" />
                        ) : (
                          <Lock className="w-8 h-8" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-semibold ${badge.earned ? 'text-amber-800' : 'text-slate-600'}`}>
                            {badge.name}
                          </h3>
                          {badge.earned && (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        <p className={`text-sm ${badge.earned ? 'text-amber-700' : 'text-slate-500'}`}>
                          {badge.description}
                        </p>
                        
                        {!badge.earned && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-slate-500">Progress</span>
                              <span className="text-slate-600 font-medium">{badge.progress}%</span>
                            </div>
                            <Progress 
                              value={badge.progress} 
                              className="h-2"
                            />
                          </div>
                        )}
                        
                        {badge.earned && (
                          <div className="mt-2 flex items-center gap-1 text-amber-600 text-sm">
                            <Star className="w-4 h-4 fill-amber-500" />
                            <span>Earned!</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Encouragement */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardContent className="p-6 text-center">
            <Trophy className="w-12 h-12 mx-auto mb-3 text-purple-500" />
            <h3 className="font-semibold text-purple-800 text-lg mb-2">
              Keep Writing!
            </h3>
            <p className="text-purple-600 text-sm">
              Every story you write helps you earn more badges and become a better writer.
              Practice makes progress!
            </p>
            <Link to={`/daily-writing/${childId}`}>
              <Button className="mt-4 bg-gradient-to-r from-purple-500 to-indigo-500">
                <Pencil className="w-4 h-4 mr-2" />
                Start Writing
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default WritingBadges;
